import { useEffect, useMemo, useState } from "react";
import { ClipboardList, Loader2, Plus, Save, Send, Tag, Ticket, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatPrice } from "@/components/site/EventCard";

const DEFAULT_VAT = 21;

function toDateTimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function toIso(local) { return local ? new Date(local).toISOString() : null; }

function euros(cents) {
  return cents == null ? "" : (cents / 100).toString().replace(".", ",");
}
function parseEuros(str) {
  const s = String(str || "").trim().replace(",", ".");
  if (!s) return 0;
  const n = parseFloat(s);
  if (isNaN(n) || n < 0) return null;
  return Math.round(n * 100);
}

function emptyForm() {
  return {
    name: "",
    description: "",
    category: "",
    price_euros: "",
    vat_rate: DEFAULT_VAT,
    capacity: "",
    sale_starts_at: "",
    sale_ends_at: "",
    sort_order: 0,
    active: true,
    pay_on_location: false,
  };
}

function TypeRow({ tt, onSaved, onDeleted, authAxios }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: tt.name || "",
    description: tt.description || "",
    category: tt.category || "",
    price_euros: euros(tt.price_cents),
    vat_rate: tt.vat_rate ?? DEFAULT_VAT,
    capacity: tt.capacity == null ? "" : String(tt.capacity),
    sale_starts_at: toDateTimeLocal(tt.sale_starts_at),
    sale_ends_at: toDateTimeLocal(tt.sale_ends_at),
    sort_order: tt.sort_order ?? 0,
    active: tt.active !== false,
    pay_on_location: !!tt.pay_on_location,
  });

  const save = async () => {
    const cents = parseEuros(form.price_euros);
    if (cents == null) return toast.error("Ongeldige prijs");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        category: form.category.trim() || null,
        price_cents: cents,
        vat_rate: Number(form.vat_rate) || 0,
        capacity: form.capacity === "" ? null : Number(form.capacity),
        sale_starts_at: form.sale_starts_at ? toIso(form.sale_starts_at) : null,
        sale_ends_at: form.sale_ends_at ? toIso(form.sale_ends_at) : null,
        sort_order: Number(form.sort_order) || 0,
        active: !!form.active,
        pay_on_location: !!form.pay_on_location,
      };
      await authAxios.patch(`/admin/ticket-types/${tt.id}`, payload);
      toast.success("Tickettype bijgewerkt");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`"${tt.name}" verwijderen?`)) return;
    try {
      const { data } = await authAxios.delete(`/admin/ticket-types/${tt.id}`);
      toast.success(data.deactivated ? "Op non-actief gezet (er waren al verkopen)" : "Verwijderd");
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const soldOut = tt.capacity != null && (tt.sold_count ?? 0) >= tt.capacity;

  if (!editing) {
    return (
      <div
        data-testid={`admin-tt-row-${tt.id}`}
        className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center px-5 py-4 bg-svj-card border-b border-svj-border last:border-b-0"
      >
        <div className="md:col-span-4">
          <div className="text-white font-semibold flex items-center gap-2">
            {tt.name}
            {!tt.active && <span className="text-[10px] uppercase tracking-widest bg-svj-border text-slate-400 rounded-full px-2 py-0.5">Inactief</span>}
            {soldOut && <span className="text-[10px] uppercase tracking-widest bg-svj-orange/20 text-svj-orange rounded-full px-2 py-0.5">Uitverkocht</span>}
            {tt.pay_on_location && <span className="text-[10px] uppercase tracking-widest bg-svj-cyan/15 text-svj-cyan rounded-full px-2 py-0.5">Betaal op locatie</span>}
          </div>
          {tt.category && <div className="text-xs text-svj-cyan">{tt.category}</div>}
          {tt.description && <div className="text-xs text-slate-400 truncate">{tt.description}</div>}
        </div>
        <div className="md:col-span-2 text-sm">
          <span className="text-svj-cyan font-bold">{formatPrice(tt.price_cents)}</span>
          <span className="text-slate-500 text-xs"> · {tt.vat_rate}% btw</span>
        </div>
        <div className="md:col-span-3 text-sm">
          <span className="text-white font-semibold">{tt.sold_count ?? 0}</span>
          <span className="text-slate-400"> / {tt.capacity == null ? "∞" : tt.capacity} verkocht</span>
        </div>
        <div className="md:col-span-3 flex md:justify-end gap-2">
          <button type="button" onClick={() => setEditing(true)} data-testid={`admin-tt-edit-${tt.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">
            Bewerken
          </button>
          <button type="button" onClick={del} data-testid={`admin-tt-delete-${tt.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
            <Trash2 className="h-3.5 w-3.5" /> Verwijder
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 py-5 bg-svj-bg/60 border-b border-svj-border" data-testid={`admin-tt-editing-${tt.id}`}>
      <TicketTypeFields form={form} setForm={setForm} idPrefix={`tt-${tt.id}`} />
      <div className="mt-4 flex gap-2 justify-end">
        <button type="button" onClick={() => setEditing(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-sm font-semibold px-4 py-2 inline-flex items-center gap-1">
          <X className="h-4 w-4" /> Annuleren
        </button>
        <button type="button" onClick={save} disabled={saving} data-testid={`admin-tt-save-${tt.id}`} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 py-2 inline-flex items-center gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Opslaan
        </button>
      </div>
    </div>
  );
}

function TicketTypeFields({ form, setForm, idPrefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <label className="block sm:col-span-2 lg:col-span-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Naam *</span>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${idPrefix}-name`} className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </label>
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Prijs (€ incl. btw) *</span>
        <input value={form.price_euros} onChange={(e) => setForm({ ...form, price_euros: e.target.value })} data-testid={`${idPrefix}-price`} placeholder="12,50" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </label>
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Btw %</span>
        <select value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: Number(e.target.value) })} data-testid={`${idPrefix}-vat`} className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
          <option value={0}>0%</option>
          <option value={9}>9% (laag)</option>
          <option value={21}>21% (hoog)</option>
        </select>
      </label>
      <label className="block sm:col-span-2 lg:col-span-4">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Omschrijving</span>
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid={`${idPrefix}-desc`} className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </label>
      <label className="block sm:col-span-2 lg:col-span-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Categorie (optioneel — groepeert tickets op de eventpagina)</span>
        <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="bv. Toegangskaarten, Weekend tickets" data-testid={`${idPrefix}-category`} className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </label>
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Capaciteit (leeg = ∞)</span>
        <input type="number" min="0" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid={`${idPrefix}-capacity`} className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </label>
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Volgorde</span>
        <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid={`${idPrefix}-sort`} className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </label>
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Verkoop start</span>
        <input type="datetime-local" value={form.sale_starts_at} onChange={(e) => setForm({ ...form, sale_starts_at: e.target.value })} data-testid={`${idPrefix}-sale-start`} className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </label>
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Verkoop eind</span>
        <input type="datetime-local" value={form.sale_ends_at} onChange={(e) => setForm({ ...form, sale_ends_at: e.target.value })} data-testid={`${idPrefix}-sale-end`} className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </label>
      <label className="flex items-center gap-2 self-end pb-1">
        <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid={`${idPrefix}-active`} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
        <span className="text-xs text-slate-300">Actief (te koop)</span>
      </label>
      <label className="flex items-center gap-2 self-end pb-1 sm:col-span-2 lg:col-span-2">
        <input type="checkbox" checked={!!form.pay_on_location} onChange={(e) => setForm({ ...form, pay_on_location: e.target.checked })} data-testid={`${idPrefix}-pay-on-location`} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
        <span className="text-xs text-slate-300">Betaling op locatie (prijs wordt getoond, maar geen online afrekenen — klant meldt zich direct aan en betaalt ter plekke)</span>
      </label>
    </div>
  );
}

// Multi-day registration ("Lichtjestour"-style events) day + custom-field selection
// per manually-issued ticket unit -- the same shape RegistrationUnitIn/
// _validate_multiday_registration_units expects, mirroring Checkout.jsx's
// emptyRegistrationUnit/RegistrationUnitBlock exactly (never a second data model).
function emptyRegistrationUnit(fieldDefs) {
  return {
    event_day_ids: [],
    field_values: Object.fromEntries((fieldDefs || []).map((f) => [f.key, f.field_type === "checkbox" ? false : ""])),
  };
}

// Timeslot booking ("Sinterklaas huisbezoeken"-style events) -- slot choice
// (one or more CONSECUTIVE slots, same "combine into one longer visit" rule
// as checkout) per manually-issued ticket unit, the admin-side counterpart of
// TimeslotBookingUnitIn/Checkout.jsx's emptyTimeslotUnit. Only reachable when
// the event has BOTH timeslot_booking_enabled and the stricter
// timeslot_manual_bookings_enabled sub-flag -- this is how "al gemaakte
// afspraken" (e.g. telefonisch geregeld) get blocked directly, without a
// customer going through paid checkout.
function emptyTimeslotUnit(fieldDefs) {
  return {
    category_id: "",
    timeslot_ids: [],
    field_values: Object.fromEntries((fieldDefs || []).map((f) => [f.key, f.field_type === "checkbox" ? false : ""])),
  };
}

// A field with depends_on_timeslot_category_id only shows once THIS unit's
// own chosen category matches; depends_on_field_id works the same way
// Checkout.jsx's own fieldIsVisible does. Kept as a small, local
// reimplementation (not a shared import) -- same convention as this file's
// other admin-side counterparts of a Checkout.jsx concept. Exported so
// TimeslotSchedule.jsx's own book/edit-booking panels (row = timeslot, not
// row = order item) can reuse the identical rule instead of a fourth copy.
export function manualFieldIsVisible(field, values, fieldsById, categoryId) {
  if (field.depends_on_timeslot_category_id) {
    return field.depends_on_timeslot_category_id === categoryId;
  }
  if (!field.depends_on_field_id) return true;
  const controller = fieldsById[field.depends_on_field_id];
  if (!controller) return true;
  const actual = String(values?.[controller.key] ?? "").trim().toLowerCase();
  const trigger = String(field.depends_on_value ?? "").trim().toLowerCase();
  return actual === trigger;
}

// Picker for one manually-booked ticket unit's slot(s) -- mirrors
// Checkout.jsx's own TimeslotBookingUnitBlock: when the event has
// categories, a category is chosen first (client-side filter dimension
// only, never sent to the backend) and the date/slot picker below is
// scoped to that category's own slots -- without this, two categories'
// slots at the same clock time rendered as two indistinguishable buttons
// with no way to tell them apart, and every category's own custom fields
// showed at once regardless of which category was actually being booked.
function AdminTimeslotUnitPicker({ idx, value, timeslots, categories, fieldDefs, fieldsById, onChange }) {
  const visibleFieldDefs = fieldDefs.filter((f) => manualFieldIsVisible(f, value.field_values, fieldsById, value.category_id));
  const relevantSlots = useMemo(
    () => (categories.length === 0 ? timeslots : timeslots.filter((s) => s.category_id === value.category_id)),
    [timeslots, categories, value.category_id],
  );
  const timeslotIds = useMemo(() => value.timeslot_ids || [], [value.timeslot_ids]);
  const selectedSlots = useMemo(
    () =>
      timeslotIds
        .map((id) => timeslots.find((s) => s.id === id))
        .filter(Boolean)
        .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")),
    [timeslotIds, timeslots],
  );
  const firstSelected = selectedSlots[0];
  const lastSelected = selectedSlots[selectedSlots.length - 1];

  const byDate = useMemo(() => {
    const groups = {};
    for (const s of relevantSlots) (groups[s.date] = groups[s.date] || []).push(s);
    for (const date of Object.keys(groups)) groups[date].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    return groups;
  }, [relevantSlots]);
  const dates = useMemo(() => Object.keys(byDate).sort(), [byDate]);

  const toggleSlot = (slot) => {
    // `full` already reflects a cross-category overlap conflict too (e.g. a
    // booking in "Huisbezoek Dronten" also blocks the same clock time in
    // "Huisbezoek Biddinghuizen") -- GET /admin/events/{id}/timeslots
    // computes this server-side; fall back to the plain capacity check if an
    // older response ever lacks the field.
    const full = !timeslotIds.includes(slot.id) && (slot.full ?? (slot.booked_count || 0) >= slot.capacity);
    if (full) return;
    if (!selectedSlots.length || slot.date !== firstSelected.date || slot.category_id !== firstSelected.category_id) {
      onChange(idx, { timeslot_ids: [slot.id] });
      return;
    }
    const groupSlots = byDate[slot.date].filter((s) => s.category_id === firstSelected.category_id);
    const slotIdx = groupSlots.findIndex((s) => s.id === slot.id);
    const firstIdx = groupSlots.findIndex((s) => s.id === firstSelected.id);
    const lastIdx = groupSlots.findIndex((s) => s.id === lastSelected.id);
    if (slotIdx === lastIdx && selectedSlots.length > 1) {
      onChange(idx, { timeslot_ids: selectedSlots.slice(0, -1).map((s) => s.id) });
    } else if (slotIdx === firstIdx && selectedSlots.length > 1) {
      onChange(idx, { timeslot_ids: selectedSlots.slice(1).map((s) => s.id) });
    } else if (slotIdx === lastIdx + 1) {
      onChange(idx, { timeslot_ids: [...selectedSlots.map((s) => s.id), slot.id] });
    } else if (slotIdx === firstIdx - 1) {
      onChange(idx, { timeslot_ids: [slot.id, ...selectedSlots.map((s) => s.id)] });
    } else if (slotIdx === firstIdx && selectedSlots.length === 1) {
      onChange(idx, { timeslot_ids: [] });
    } else {
      onChange(idx, { timeslot_ids: [slot.id] });
    }
  };

  return (
    <div className="rounded-lg border border-svj-border bg-svj-bg/60 p-3 space-y-3" data-testid={`admin-manual-ticket-timeslot-${idx}`}>
      {categories.length > 0 && (
        <div>
          <span className="text-[10px] text-slate-400">Categorie *</span>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {categories.map((c) => {
              const checked = value.category_id === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onChange(idx, { category_id: c.id, timeslot_ids: [] })}
                  data-testid={`admin-manual-ticket-timeslot-${idx}-category-${c.id}`}
                  className={`rounded-lg border px-2.5 py-1.5 text-left text-xs font-semibold transition-colors ${
                    checked ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan" : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                  }`}
                >
                  <div>{c.name}</div>
                  <div className="mt-0.5 text-[10px] font-normal opacity-80">
                    {c.price_cents === 0 ? "Gratis" : formatPrice(c.price_cents)} · {c.duration_minutes} min
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {(categories.length === 0 || value.category_id) && (
      <div>
        <span className="text-[10px] text-slate-400">
          Datum + tijdslot * <span className="normal-case font-normal text-slate-500">(tik meerdere aaneengesloten tijdsloten aan voor een langer bezoek)</span>
        </span>
        <div className="mt-1.5 space-y-2">
          {dates.length === 0 && <div className="text-[10px] text-slate-500">Geen tijdsloten beschikbaar voor deze categorie.</div>}
          {dates.map((date) => (
            <div key={date}>
              <div className="text-[10px] text-slate-500 mb-1">
                {new Date(`${date}T00:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}
              </div>
              <div className="flex flex-wrap gap-2">
                {byDate[date].map((s) => {
                  const checked = timeslotIds.includes(s.id);
                  const full = !checked && (s.full ?? (s.booked_count || 0) >= s.capacity);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleSlot(s)}
                      disabled={full}
                      data-testid={`admin-manual-ticket-timeslot-${idx}-slot-${s.id}`}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                        checked
                          ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                          : full
                            ? "bg-svj-bg border-svj-border text-slate-600 cursor-not-allowed opacity-60"
                            : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                      }`}
                    >
                      {(s.start_time || "").slice(0, 5)} - {(s.end_time || "").slice(0, 5)}
                      {full && <span className="ml-1 text-[9px] uppercase text-red-400">vol</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {selectedSlots.length > 1 && (
          <div className="mt-1.5 text-[10px] text-svj-cyan">{selectedSlots.length} tijdsloten samengevoegd tot één bezoek.</div>
        )}
      </div>
      )}
      {visibleFieldDefs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {visibleFieldDefs.map((f) => (
            <label key={f.key} className="block">
              <span className="text-[10px] text-slate-400">{f.label}{f.required && " *"}</span>
              <ManualRegistrationFieldInput
                field={f}
                value={value.field_values[f.key]}
                idPrefix={`admin-manual-ticket-timeslot-${idx}-field-${f.key}`}
                onChange={(v) => onChange(idx, { field_values: { ...value.field_values, [f.key]: v } })}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Exported so TimeslotSchedule.jsx's own manual-booking panel (an empty slot
// blocked directly from the Presentie rooster) can reuse the identical custom
// registration-field input instead of duplicating it.
export function ManualRegistrationFieldInput({ field, value, onChange, idPrefix }) {
  const commonClass = "mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan";
  if (field.field_type === "textarea") {
    return <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={idPrefix} className={`${commonClass} h-auto`} />;
  }
  if (field.field_type === "select") {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={idPrefix} className={commonClass}>
        <option value="">Kies…</option>
        {(field.options || []).map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }
  if (field.field_type === "checkbox") {
    return (
      <label className="mt-2 flex items-center gap-2">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={idPrefix} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
        <span className="text-xs text-slate-300">Ja</span>
      </label>
    );
  }
  const inputType = field.field_type === "number" ? "number" : field.field_type === "email" ? "email" : field.field_type === "phone" ? "tel" : "text";
  return <input type={inputType} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={idPrefix} className={commonClass} />;
}

// Lets the admin create a brand-new, custom one-off timeslot directly inside
// "Handmatig ticket aanmaken" instead of only being able to pick from
// already-generated slots -- e.g. a one-off 16:15-17:15 slot for one specific
// company. Mirrors TimeslotSchedule.jsx's own AddSlotPanel (same fields, same
// category-prefill behavior), reimplemented locally per this file's own
// convention (this file already has its own euros/parseEuros helpers).
function InlineAddTimeslotPanel({ eventId, authAxios, categoryOptions, onCancel, onCreated }) {
  const [form, setForm] = useState({ date: "", start_time: "", end_time: "", price: "0", capacity: "1", category_id: "" });
  const [creating, setCreating] = useState(false);

  const applyCategory = (categoryId) => {
    const cat = categoryOptions.find((c) => c.id === categoryId);
    if (!cat) {
      setForm((f) => ({ ...f, category_id: "" }));
      return;
    }
    let end = form.end_time;
    if (form.start_time) {
      const [h, m] = form.start_time.split(":").map(Number);
      const total = h * 60 + m + cat.duration_minutes;
      end = `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
    }
    setForm((f) => ({ ...f, category_id: categoryId, price: euros(cat.price_cents), end_time: end }));
  };

  const create = async () => {
    if (!form.date || !form.start_time || !form.end_time) return toast.error("Datum, starttijd en eindtijd zijn verplicht");
    const price_cents = parseEuros(form.price);
    if (price_cents == null) return toast.error("Ongeldige prijs");
    setCreating(true);
    try {
      const { data } = await authAxios.post(`/admin/events/${eventId}/timeslots`, {
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        price_cents,
        capacity: Math.max(1, Number(form.capacity) || 1),
        category_id: form.category_id || null,
      });
      toast.success("Tijdslot toegevoegd");
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="rounded-lg border border-svj-cyan/40 bg-svj-bg px-3 py-3 space-y-2" data-testid="admin-manual-ticket-add-timeslot-panel">
      <div className="text-xs font-semibold text-white">Nieuw tijdslot aanmaken</div>
      {categoryOptions.length > 0 && (
        <select
          value={form.category_id}
          onChange={(e) => applyCategory(e.target.value)}
          data-testid="admin-manual-ticket-add-timeslot-category"
          className="h-9 w-full px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm"
        >
          <option value="">Geen categorie</option>
          {categoryOptions.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({euros(c.price_cents)} € / {c.duration_minutes} min)</option>
          ))}
        </select>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} type="date" data-testid="admin-manual-ticket-add-timeslot-date" className="h-9 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm" />
        <input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} type="time" data-testid="admin-manual-ticket-add-timeslot-start" className="h-9 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm" />
        <input value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} type="time" data-testid="admin-manual-ticket-add-timeslot-end" className="h-9 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm" />
        <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} type="text" inputMode="decimal" placeholder="Prijs (€)" data-testid="admin-manual-ticket-add-timeslot-price" className="h-9 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm" />
        <input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} type="number" min="1" placeholder="Capaciteit" data-testid="admin-manual-ticket-add-timeslot-capacity" className="h-9 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm" />
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="rounded-lg border bg-svj-card border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
        <button
          type="button"
          onClick={create}
          disabled={creating}
          data-testid="admin-manual-ticket-add-timeslot-submit"
          className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50"
        >
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Toevoegen
        </button>
      </div>
    </div>
  );
}

function emptyManualForm() {
  return {
    ticket_type_id: "",
    qty: 1,
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    send_confirmation_email: true,
    // paid_now: al betaald (bv. contant/overschrijving) - volledige prijs, direct geldig.
    // free: gratis/comp (bv. sponsoren) - bedrag wordt €0, direct geldig.
    // payment_link: nog niet betaald - echte Pay.nl-betaallink wordt gemaild, ticket
    // wordt pas aangemaakt zodra de klant daadwerkelijk betaalt.
    payment_mode: "paid_now",
    // Only meaningful when payment_mode === "payment_link": reserves the
    // ticket-type/timeslot capacity right now but does NOT yet send any
    // payment link or email -- lets an admin take several phone-in bookings
    // throughout the day (each locking in its own slot immediately) and send
    // every payment link out together later, from the "Bestellingen"
    // overview, via the bulk send-payment-links action.
    defer_payment_link: false,
    participants: [{ first_name: "", last_name: "", route_group_id: "", medal_number: "" }],
    registration_units: [emptyRegistrationUnit([])],
    timeslot_units: [emptyTimeslotUnit([])],
  };
}

// Reuses the exact same shared logic every real checkout goes through
// (POST /admin/events/{event_id}/tickets/manual -> _reserve_capacity +
// _finalize_paid_order server-side) - a manually issued ticket is a real ticket,
// with a real code, real PDF and real presence/scan behavior, just without Pay.nl.
function ManualTicketSection({ eventId, items, companionAppEnabled, multiDayRegistrationEnabled, timeslotBookingEnabled, timeslotManualBookingsEnabled, authAxios }) {
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyManualForm());
  const [routeGroups, setRouteGroups] = useState([]);
  const [days, setDays] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [timeslots, setTimeslots] = useState([]);
  const [timeslotCategories, setTimeslotCategories] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [addingSlot, setAddingSlot] = useState(false);
  // Id -> full field lookup for resolving a field's depends_on_field_id back
  // to the controlling field's own key, same convention as Checkout.jsx's
  // own registrationFieldsById.
  const fieldsById = useMemo(() => Object.fromEntries(fieldDefs.map((f) => [f.id, f])), [fieldDefs]);
  // The backend rejects timeslot_units entirely unless BOTH flags are on (see
  // admin_create_manual_order's is_timeslot_booking gate) -- a timeslot-booking
  // event with the sub-flag off simply has no way to issue a manual ticket at
  // all (there is no other price source), so the whole section is disabled
  // rather than silently submitting something the backend will 403.
  const timeslotManualBlocked = timeslotBookingEnabled && !timeslotManualBookingsEnabled;

  useEffect(() => {
    if (!companionAppEnabled || !eventId) { setRouteGroups([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAxios.get(`/admin/events/${eventId}/route-groups`);
        if (!cancelled) setRouteGroups(data.items || []);
      } catch {
        if (!cancelled) setRouteGroups([]);
      }
    })();
    return () => { cancelled = true; };
  }, [authAxios, companionAppEnabled, eventId]);

  // Fetched once on mount AND re-fetched every time the form is opened (see
  // openAdding below) -- NOT just on mount. The event admin workspace (Sprint 09J)
  // keeps every section's components mounted simultaneously (CSS-hidden, never
  // unmounted) so an admin can add a registration field in the Aanmeldformulier
  // tab and then switch to Tickets & verkoop in the SAME page load without a
  // reload. A mount-only fetch would leave this component's fieldDefs stale --
  // the manually-created ticket's form would silently omit the brand-new field
  // (never shown, never collected, so never staged/rendered) -- a real production
  // bug: a field added this way looked like it "doesn't show up automatically in
  // the confirmation email" when the real cause was the admin UI never asking for
  // it in the first place.
  const refreshRegistrationData = async () => {
    if (!eventId || (!multiDayRegistrationEnabled && !timeslotBookingEnabled)) {
      setDays([]);
      setFieldDefs([]);
      setTimeslots([]);
      setTimeslotCategories([]);
      return;
    }
    try {
      const requests = [authAxios.get(`/admin/events/${eventId}/registration-fields`)];
      if (multiDayRegistrationEnabled) requests.push(authAxios.get(`/admin/events/${eventId}/days`));
      if (timeslotBookingEnabled) {
        requests.push(authAxios.get(`/admin/events/${eventId}/timeslots`));
        requests.push(authAxios.get(`/admin/events/${eventId}/timeslot-categories`));
      }
      const [fieldsRes, ...rest] = await Promise.all(requests);
      setFieldDefs((fieldsRes.data.items || []).filter((f) => f.active));
      if (multiDayRegistrationEnabled) setDays(rest.shift()?.data.items || []);
      else setDays([]);
      if (timeslotBookingEnabled) {
        setTimeslots(rest.shift()?.data.items || []);
        setTimeslotCategories((rest.shift()?.data.items || []).filter((c) => c.active));
      } else {
        setTimeslots([]);
        setTimeslotCategories([]);
      }
    } catch {
      setDays([]);
      setFieldDefs([]);
      setTimeslots([]);
      setTimeslotCategories([]);
    }
  };

  useEffect(() => {
    refreshRegistrationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios, multiDayRegistrationEnabled, timeslotBookingEnabled, eventId]);

  // Opens the add-ticket form AND refreshes days/fieldDefs first, so a field
  // added moments ago elsewhere on this same (never-unmounted) page is already
  // present by the time the registration-units inputs render below.
  const openAdding = async () => {
    await refreshRegistrationData();
    setAdding(true);
  };

  const setQty = (raw) => {
    const qty = Math.max(1, Math.min(20, Number(raw) || 1));
    setForm((f) => {
      const participants = Array.from({ length: qty }, (_, i) => f.participants[i] || { first_name: "", last_name: "", route_group_id: "", medal_number: "" });
      const registration_units = Array.from({ length: qty }, (_, i) => f.registration_units[i] || emptyRegistrationUnit(fieldDefs));
      const timeslot_units = Array.from({ length: qty }, (_, i) => f.timeslot_units[i] || emptyTimeslotUnit(fieldDefs));
      return { ...f, qty, participants, registration_units, timeslot_units };
    });
  };

  const setTimeslotUnit = (idx, patch) => {
    setForm((f) => ({
      ...f,
      timeslot_units: f.timeslot_units.map((u, i) => (i === idx ? { ...u, ...patch } : u)),
    }));
  };

  const setParticipant = (idx, patch) => {
    setForm((f) => ({
      ...f,
      participants: f.participants.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    }));
  };

  const setRegistrationUnit = (idx, patch) => {
    setForm((f) => ({
      ...f,
      registration_units: f.registration_units.map((u, i) => (i === idx ? { ...u, ...patch } : u)),
    }));
  };

  const toggleRegistrationDay = (idx, dayId) => {
    setForm((f) => ({
      ...f,
      registration_units: f.registration_units.map((u, i) => {
        if (i !== idx) return u;
        const set = new Set(u.event_day_ids);
        if (set.has(dayId)) set.delete(dayId);
        else set.add(dayId);
        return { ...u, event_day_ids: Array.from(set) };
      }),
    }));
  };

  const create = async () => {
    if (!form.ticket_type_id) return toast.error("Kies een tickettype");
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("Voor- en achternaam van de koper zijn verplicht");
    if (!form.email.trim()) return toast.error("E-mailadres van de koper is verplicht");
    if (companionAppEnabled) {
      for (const p of form.participants) {
        if (!p.first_name.trim() || !p.last_name.trim()) {
          return toast.error("Voor- en achternaam zijn verplicht voor elke deelnemer");
        }
      }
    }
    if (multiDayRegistrationEnabled) {
      for (const u of form.registration_units) {
        if (!u.event_day_ids.length) {
          return toast.error("Kies minstens één dag voor elk ticket");
        }
        for (const f of fieldDefs) {
          if (f.required && !String(u.field_values[f.key] ?? "").trim()) {
            return toast.error(`Veld '${f.label}' is verplicht`);
          }
        }
      }
    }
    if (timeslotBookingEnabled) {
      if (timeslotManualBlocked) return toast.error("Handmatige blokkering van tijdsloten staat uit voor dit event");
      for (const u of form.timeslot_units) {
        if (timeslotCategories.length > 0 && !u.category_id) {
          return toast.error("Kies een categorie voor elk ticket");
        }
        if (!u.timeslot_ids?.length) {
          return toast.error("Kies een tijdslot voor elk ticket");
        }
        for (const f of fieldDefs) {
          if (!manualFieldIsVisible(f, u.field_values, fieldsById, u.category_id)) continue;
          if (f.required && !String(u.field_values[f.key] ?? "").trim()) {
            return toast.error(`Veld '${f.label}' is verplicht`);
          }
        }
      }
    }
    setCreating(true);
    try {
      const payload = {
        ticket_type_id: form.ticket_type_id,
        qty: form.qty,
        customer: {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
        },
        send_confirmation_email: !!form.send_confirmation_email,
        free: form.payment_mode === "free",
        send_payment_link: form.payment_mode === "payment_link",
        defer_payment_link: form.payment_mode === "payment_link" && !!form.defer_payment_link,
        ...(companionAppEnabled
          ? {
              participants: form.participants.map((p) => ({
                first_name: p.first_name.trim(),
                last_name: p.last_name.trim(),
                route_group_id: p.route_group_id || null,
                medal_number: p.medal_number.trim() || null,
              })),
            }
          : {}),
        ...(multiDayRegistrationEnabled
          ? {
              registration_units: form.registration_units.map((u) => ({
                event_day_ids: u.event_day_ids,
                field_values: u.field_values,
              })),
            }
          : {}),
        ...(timeslotBookingEnabled
          ? {
              timeslot_units: form.timeslot_units.map((u) => ({
                timeslot_ids: u.timeslot_ids,
                field_values: u.field_values,
              })),
            }
          : {}),
      };
      const { data } = await authAxios.post(`/admin/events/${eventId}/tickets/manual`, payload);
      if (data.mode === "payment_link" && data.deferred) {
        setLastResult({ mode: "payment_link", deferred: true });
        toast.success("Tijdslot gereserveerd — betaallink kan later verstuurd worden vanuit Bestellingen");
      } else if (data.mode === "payment_link") {
        setLastResult({ mode: "payment_link", paymentUrl: data.payment_url, emailSent: !!data.email?.sent });
        toast.success(data.email?.sent ? "Betaallink aangemaakt en gemaild" : "Betaallink aangemaakt");
      } else {
        setLastResult({ mode: "tickets", tickets: data.tickets || [] });
        toast.success(`${(data.tickets || []).length} ticket(s) aangemaakt`);
      }
      setForm(emptyManualForm());
      setAdding(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="rounded-2xl border border-svj-border overflow-hidden bg-svj-card" data-testid="admin-manual-ticket-section">
      <div className="px-5 py-4 flex items-center justify-between border-b border-svj-border">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Handmatig ticket aanmaken</div>
        </div>
        {!adding && !timeslotManualBlocked && (
          <button type="button" onClick={openAdding} data-testid="admin-manual-ticket-add" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Ticket
          </button>
        )}
      </div>

      {timeslotManualBlocked ? (
        <p className="px-5 py-4 text-xs text-slate-400" data-testid="admin-manual-ticket-blocked">
          Handmatige blokkering van tijdsloten staat uit voor dit event — alleen een superadmin kan dit
          aanzetten (Instellingen → "Zet handmatige blokkering aan").
        </p>
      ) : (
        <p className="px-5 pt-4 text-xs text-slate-400">
          Maakt een echt ticket aan — handig voor testaccounts, comps, sponsoren, support of (bij tijdslot-
          boekingen) een al gemaakte afspraak. Slaat de verkoopperiode en actief/inactief-status van het
          tickettype over.
        </p>
      )}

      {adding && !timeslotManualBlocked && (
        <div className="px-5 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
            {[
              { value: "paid_now", label: "Al betaald", hint: "bv. contant/overschrijving — volledige prijs, direct geldig" },
              { value: "free", label: "Gratis (test/comp)", hint: "bv. sponsoren — bedrag wordt €0, direct geldig" },
              { value: "payment_link", label: "Stuur betaallink", hint: "nog niet betaald — ticket volgt pas na echte betaling" },
            ].map((opt) => (
              <label
                key={opt.value}
                data-testid={`admin-manual-ticket-mode-${opt.value}`}
                className={`flex flex-col gap-0.5 rounded-lg border px-3 py-2 cursor-pointer ${
                  form.payment_mode === opt.value ? "border-svj-cyan bg-svj-cyan/10" : "border-svj-border bg-svj-bg/60"
                }`}
              >
                <span className="flex items-center gap-2 text-xs font-semibold text-white">
                  <input
                    type="radio"
                    name="manual-ticket-payment-mode"
                    checked={form.payment_mode === opt.value}
                    onChange={() => setForm({ ...form, payment_mode: opt.value })}
                    className="h-3.5 w-3.5"
                  />
                  {opt.label}
                </span>
                <span className="text-[10px] text-slate-400">{opt.hint}</span>
              </label>
            ))}
          </div>

          {form.payment_mode === "payment_link" && (
            <label className="flex items-start gap-2 mb-4 rounded-lg border border-svj-border bg-svj-bg/60 px-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.defer_payment_link}
                onChange={(e) => setForm({ ...form, defer_payment_link: e.target.checked })}
                data-testid="admin-manual-ticket-defer"
                className="mt-0.5 h-4 w-4 rounded border-svj-border bg-svj-bg"
              />
              <span className="text-xs text-slate-300">
                <span className="font-semibold text-white">Betaallink later versturen.</span> Reserveert het
                tijdslot/ticket meteen, maar stuurt nog geen betaallink of mail — handig om eerst meerdere
                telefonische boekingen vast te leggen en de betaallinks daarna in één keer te versturen vanuit
                Bestellingen.
              </span>
            </label>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block sm:col-span-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Tickettype *</span>
              <select value={form.ticket_type_id} onChange={(e) => setForm({ ...form, ticket_type_id: e.target.value })} data-testid="admin-manual-ticket-type" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
                <option value="">Kies een tickettype…</option>
                {items.map((tt) => (
                  <option key={tt.id} value={tt.id}>{tt.name} — {formatPrice(tt.price_cents)}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Aantal</span>
              <input type="number" min="1" max="20" value={form.qty} onChange={(e) => setQty(e.target.value)} data-testid="admin-manual-ticket-qty" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </label>
            <label className="flex items-center gap-2 self-end pb-1">
              <input type="checkbox" checked={!!form.send_confirmation_email} onChange={(e) => setForm({ ...form, send_confirmation_email: e.target.checked })} data-testid="admin-manual-ticket-send-email" className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
              <span className="text-xs text-slate-300">{form.payment_mode === "payment_link" ? "Betaallink mailen naar koper" : "Bevestigingsmail versturen"}</span>
            </label>

            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Voornaam koper *</span>
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="admin-manual-ticket-first-name" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Achternaam koper *</span>
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="admin-manual-ticket-last-name" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">E-mail koper *</span>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-manual-ticket-email" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </label>
          </div>

          {companionAppEnabled && (
            <div className="mt-4 space-y-3">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Deelnemergegevens (per ticket)</div>
              {form.participants.map((p, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 rounded-lg border border-svj-border bg-svj-bg/60 p-3">
                  <input placeholder="Voornaam *" value={p.first_name} onChange={(e) => setParticipant(idx, { first_name: e.target.value })} data-testid={`admin-manual-ticket-participant-${idx}-first-name`} className="h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
                  <input placeholder="Achternaam *" value={p.last_name} onChange={(e) => setParticipant(idx, { last_name: e.target.value })} data-testid={`admin-manual-ticket-participant-${idx}-last-name`} className="h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
                  {routeGroups.length > 0 && (
                    <select value={p.route_group_id} onChange={(e) => setParticipant(idx, { route_group_id: e.target.value })} data-testid={`admin-manual-ticket-participant-${idx}-route-group`} className="h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
                      <option value="">Routekeuze…</option>
                      {routeGroups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}{g.distance_km ? ` (${g.distance_km} km)` : ""}</option>
                      ))}
                    </select>
                  )}
                  <input placeholder="Medaillenummer" value={p.medal_number} onChange={(e) => setParticipant(idx, { medal_number: e.target.value })} data-testid={`admin-manual-ticket-participant-${idx}-medal`} className="h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
                </div>
              ))}
            </div>
          )}

          {multiDayRegistrationEnabled && (
            <div className="mt-4 space-y-3">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Aanmelding per dag (per ticket)</div>
              {days.length === 0 ? (
                <p className="text-xs text-slate-500">Er zijn nog geen dagen aangemaakt voor dit event — voeg eerst dagen toe bij "Aanmeldformulier".</p>
              ) : (
                form.registration_units.map((u, idx) => (
                  <div key={idx} className="rounded-lg border border-svj-border bg-svj-bg/60 p-3 space-y-3" data-testid={`admin-manual-ticket-registration-${idx}`}>
                    <div>
                      <span className="text-[10px] text-slate-400">Dag(en) *</span>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {days.map((d) => {
                          const checked = u.event_day_ids.includes(d.id);
                          return (
                            <button
                              key={d.id}
                              type="button"
                              onClick={() => toggleRegistrationDay(idx, d.id)}
                              data-testid={`admin-manual-ticket-registration-${idx}-day-${d.id}`}
                              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                checked
                                  ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                                  : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                              }`}
                            >
                              {d.label || `Dag ${d.day_number}`}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {fieldDefs.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {fieldDefs.map((f) => (
                          <label key={f.key} className="block">
                            <span className="text-[10px] text-slate-400">{f.label}{f.required && " *"}</span>
                            <ManualRegistrationFieldInput
                              field={f}
                              value={u.field_values[f.key]}
                              idPrefix={`admin-manual-ticket-registration-${idx}-field-${f.key}`}
                              onChange={(v) => setRegistrationUnit(idx, { field_values: { ...u.field_values, [f.key]: v } })}
                            />
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {timeslotBookingEnabled && !timeslotManualBlocked && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Tijdslot (per ticket)</div>
                {!addingSlot && (
                  <button
                    type="button"
                    onClick={() => setAddingSlot(true)}
                    data-testid="admin-manual-ticket-add-timeslot-toggle"
                    className="text-[10px] font-semibold text-svj-cyan hover:underline inline-flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Nieuw tijdslot aanmaken
                  </button>
                )}
              </div>
              {addingSlot && (
                <InlineAddTimeslotPanel
                  eventId={eventId}
                  authAxios={authAxios}
                  categoryOptions={timeslotCategories}
                  onCancel={() => setAddingSlot(false)}
                  onCreated={async (slot) => {
                    await refreshRegistrationData();
                    setAddingSlot(false);
                    // Auto-select the freshly created slot for every ticket
                    // unit that hasn't chosen one yet -- a convenience, never
                    // overriding a unit that already has its own selection.
                    setForm((f) => ({
                      ...f,
                      timeslot_units: f.timeslot_units.map((u) =>
                        u.timeslot_ids?.length ? u : { ...u, category_id: slot.category_id || "", timeslot_ids: [slot.id] },
                      ),
                    }));
                  }}
                />
              )}
              {timeslots.length === 0 && !addingSlot ? (
                <p className="text-xs text-slate-500">Er zijn nog geen tijdsloten aangemaakt voor dit event — voeg hierboven een tijdslot toe, of ga naar "Tijdsloten".</p>
              ) : (
                form.timeslot_units.map((u, idx) => (
                  <AdminTimeslotUnitPicker
                    key={idx}
                    idx={idx}
                    value={u}
                    timeslots={timeslots}
                    categories={timeslotCategories}
                    fieldDefs={fieldDefs}
                    fieldsById={fieldsById}
                    onChange={setTimeslotUnit}
                  />
                ))
              )}
            </div>
          )}

          <div className="mt-4 flex gap-2 justify-end">
            <button type="button" onClick={() => { setAdding(false); setForm(emptyManualForm()); }} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-sm font-semibold px-4 py-2 inline-flex items-center gap-1">
              <X className="h-4 w-4" /> Annuleren
            </button>
            <button type="button" onClick={create} disabled={creating} data-testid="admin-manual-ticket-create" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 py-2 inline-flex items-center gap-2">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Ticket(s) aanmaken
            </button>
          </div>
        </div>
      )}

      {!adding && lastResult?.mode === "tickets" && lastResult.tickets.length > 0 && (
        <div className="px-5 py-4 border-t border-svj-border" data-testid="admin-manual-ticket-result">
          <div className="text-xs text-slate-400 mb-2">Laatst aangemaakte ticketcode(s):</div>
          <div className="flex flex-wrap gap-2">
            {lastResult.tickets.map((t) => (
              <span key={t.id} className="font-mono text-xs px-2 py-1 rounded-md bg-svj-bg border border-svj-border text-svj-cyan">{t.code}</span>
            ))}
          </div>
        </div>
      )}

      {!adding && lastResult?.mode === "payment_link" && lastResult.deferred && (
        <div className="px-5 py-4 border-t border-svj-border" data-testid="admin-manual-ticket-result">
          <div className="text-xs text-slate-400">
            Tijdslot/ticket-capaciteit is al gereserveerd — er is nog <strong className="text-white">geen</strong> betaallink
            aangemaakt of gemaild. Stuur de betaallink naar deze (en eventueel meerdere) klant(en) wanneer je zelf wilt,
            vanuit de bestellingenlijst (status "Gereserveerd") op de Rapportage-pagina.
          </div>
        </div>
      )}

      {!adding && lastResult?.mode === "payment_link" && !lastResult.deferred && (
        <div className="px-5 py-4 border-t border-svj-border" data-testid="admin-manual-ticket-result">
          <div className="text-xs text-slate-400 mb-2">
            Betaallink aangemaakt{lastResult.emailSent ? " en gemaild naar de koper" : " (mail niet verstuurd)"}. Ticket(s) volgen automatisch zodra er echt betaald is.
          </div>
          <a href={lastResult.paymentUrl} target="_blank" rel="noreferrer" className="font-mono text-xs px-2 py-1 rounded-md bg-svj-bg border border-svj-border text-svj-cyan break-all inline-block">
            {lastResult.paymentUrl}
          </a>
        </div>
      )}
    </div>
  );
}

function WaitlistStatusCell({ row }) {
  if (row.unsubscribed_at) {
    return <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Uitgeschreven</span>;
  }
  if (row.notified_at) {
    return (
      <span className="text-[11px] uppercase tracking-widest font-semibold text-emerald-400">
        Genotificeerd · {new Date(row.notified_at).toLocaleDateString("nl-NL")}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[11px] uppercase tracking-widest font-semibold text-svj-cyan">Wachtend</span>
      {row.notify_error && (
        <span
          className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/40"
          title={row.notify_error}
          data-testid={`admin-waitlist-error-${row.id}`}
        >
          Mail mislukt
        </span>
      )}
    </div>
  );
}

function WaitlistRow({ row, ticketTypeNames, authAxios, onRemoved }) {
  const ttName = row.ticket_type_id ? (ticketTypeNames[row.ticket_type_id] || "Tickettype") : "Hele event";
  const [removing, setRemoving] = useState(false);

  const remove = async () => {
    if (!window.confirm(`${row.email} van de wachtlijst verwijderen?`)) return;
    setRemoving(true);
    try {
      await authAxios.delete(`/admin/waitlist/${row.id}`);
      toast.success("Verwijderd van de wachtlijst");
      onRemoved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div
      data-testid={`admin-waitlist-row-${row.id}`}
      className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center px-5 py-4 bg-svj-card border-b border-svj-border last:border-b-0"
    >
      <div className="md:col-span-4">
        <div className="text-white font-semibold text-sm">{row.email}</div>
        {row.first_name && <div className="text-xs text-slate-400">{row.first_name}</div>}
      </div>
      <div className="md:col-span-3 text-sm text-slate-300">{ttName}</div>
      <div className="md:col-span-2 text-xs text-slate-400">
        {row.created_at ? new Date(row.created_at).toLocaleDateString("nl-NL") : "—"}
      </div>
      <div className="md:col-span-3 flex items-center justify-between gap-2">
        <WaitlistStatusCell row={row} />
        {!row.unsubscribed_at && (
          <button
            type="button"
            onClick={remove}
            disabled={removing}
            data-testid={`admin-waitlist-remove-${row.id}`}
            title="Verwijderen van de wachtlijst"
            className="shrink-0 rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2.5 py-1.5 inline-flex items-center gap-1"
          >
            {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

function WaitlistSection({ eventId, ticketTypeNames, authAxios }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, notified: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);
  const [migrationPending, setMigrationPending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/waitlist`);
      setItems(data.items || []);
      setCounts({ pending: data.pending || 0, notified: data.notified || 0, unsubscribed: data.unsubscribed || 0 });
      setMigrationPending(!!data.migration_pending);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  return (
    <div className="rounded-2xl border border-svj-border overflow-hidden bg-svj-card" data-testid="admin-waitlist-section">
      <div className="px-5 py-4 flex items-center justify-between border-b border-svj-border">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Wachtlijst</div>
          <span className="text-xs text-slate-400">({items.length})</span>
        </div>
      </div>

      {loading && <div className="px-5 py-6 text-sm text-slate-400">Laden…</div>}

      {!loading && migrationPending && (
        <div className="px-5 py-6 text-sm text-svj-orange bg-svj-orange/10" data-testid="admin-waitlist-migration-pending">
          De wachtlijst-tabel bestaat nog niet — voer de SQL-migratie <code>add_waitlist.sql</code> uit om deze functie te activeren.
        </div>
      )}

      {!loading && !migrationPending && (
        <>
          <div className="px-5 py-4 grid grid-cols-3 gap-3 border-b border-svj-border">
            <div className="rounded-xl border border-svj-border bg-svj-bg/60 p-3 text-center">
              <div className="text-2xl font-extrabold text-white" data-testid="admin-waitlist-count-pending">{counts.pending}</div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Wachtend</div>
            </div>
            <div className="rounded-xl border border-svj-border bg-svj-bg/60 p-3 text-center">
              <div className="text-2xl font-extrabold text-emerald-400" data-testid="admin-waitlist-count-notified">{counts.notified}</div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Genotificeerd</div>
            </div>
            <div className="rounded-xl border border-svj-border bg-svj-bg/60 p-3 text-center">
              <div className="text-2xl font-extrabold text-slate-400" data-testid="admin-waitlist-count-unsubscribed">{counts.unsubscribed}</div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Uitgeschreven</div>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="px-5 py-10 text-center" data-testid="admin-waitlist-empty">
              <ClipboardList className="h-6 w-6 text-svj-cyan mx-auto" />
              <div className="mt-2 text-white font-semibold text-sm">Nog niemand op de wachtlijst</div>
              <p className="mt-1 text-xs text-slate-400">Zodra een tickettype uitverkocht raakt, kunnen bezoekers zich hier aanmelden.</p>
            </div>
          ) : (
            <div>
              <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-svj-card/60 text-[11px] uppercase tracking-widest text-slate-400 font-semibold border-b border-svj-border">
                <div className="col-span-4">E-mail</div>
                <div className="col-span-3">Tickettype</div>
                <div className="col-span-2">Aangemeld</div>
                <div className="col-span-3">Status</div>
              </div>
              {items.map((row) => (
                <WaitlistRow key={row.id} row={row} ticketTypeNames={ticketTypeNames} authAxios={authAxios} onRemoved={load} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function emptyDiscountCodeForm() {
  return { code: "", discount_type: "percentage", percentage: "10", fixed_amount_cents: "", max_uses: "", valid_from: "", valid_until: "", active: true };
}

function DiscountCodeRow({ row, eventId, authAxios, onChanged }) {
  const [busy, setBusy] = useState(false);

  const toggleActive = async () => {
    setBusy(true);
    try {
      await authAxios.patch(`/admin/discount-codes/${row.id}`, { active: !row.active });
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Kortingscode "${row.code}" verwijderen?`)) return;
    setBusy(true);
    try {
      await authAxios.delete(`/admin/discount-codes/${row.id}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const valueLabel = row.discount_type === "percentage" ? `${row.percentage}%` : formatPrice(row.fixed_amount_cents);
  const usageLabel = `${row.used_count || 0}/${row.max_uses ?? "∞"}`;
  const windowLabel = [
    row.valid_from ? `vanaf ${new Date(row.valid_from).toLocaleDateString("nl-NL")}` : null,
    row.valid_until ? `t/m ${new Date(row.valid_until).toLocaleDateString("nl-NL")}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-svj-border" data-testid={`admin-discount-code-row-${row.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <code className="font-mono text-sm font-bold text-svj-cyan">{row.code}</code>
          <span className="text-xs text-slate-300">{valueLabel} korting</span>
          {!row.active && <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Uit</span>}
        </div>
        <div className="mt-0.5 text-[11px] text-slate-400">
          Gebruikt: {usageLabel}{windowLabel ? ` · ${windowLabel}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" onClick={toggleActive} disabled={busy} data-testid={`admin-discount-code-toggle-${row.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-svj-border text-slate-300 hover:text-white">
          {row.active ? "Uitzetten" : "Aanzetten"}
        </button>
        <button type="button" onClick={remove} disabled={busy} data-testid={`admin-discount-code-delete-${row.id}`} className="text-red-400 hover:text-red-300 p-1.5 rounded-lg">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function DiscountCodesSection({ eventId, authAxios }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyDiscountCodeForm());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/discount-codes`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const create = async () => {
    if (!form.code.trim()) return toast.error("Vul een code in");
    if (form.discount_type === "percentage") {
      const pct = parseFloat(form.percentage);
      if (isNaN(pct) || pct <= 0 || pct > 100) return toast.error("Percentage moet tussen 0 en 100 liggen");
    } else {
      const cents = parseEuros(form.fixed_amount_cents);
      if (!cents || cents <= 0) return toast.error("Vul een geldig kortingsbedrag in");
    }
    setCreating(true);
    try {
      const payload = {
        code: form.code.trim(),
        discount_type: form.discount_type,
        percentage: form.discount_type === "percentage" ? parseFloat(form.percentage) : null,
        fixed_amount_cents: form.discount_type === "fixed" ? parseEuros(form.fixed_amount_cents) : null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        valid_from: toIso(form.valid_from),
        valid_until: toIso(form.valid_until),
        active: true,
      };
      await authAxios.post(`/admin/events/${eventId}/discount-codes`, payload);
      toast.success("Kortingscode aangemaakt");
      setForm(emptyDiscountCodeForm());
      setAdding(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="rounded-2xl border border-svj-border overflow-hidden bg-svj-card" data-testid="admin-discount-codes-section">
      <div className="px-5 py-4 flex items-center justify-between border-b border-svj-border">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Kortingscodes</div>
          <span className="text-xs text-slate-400">({items.length})</span>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} data-testid="admin-discount-code-add" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Code
          </button>
        )}
      </div>

      {adding && (
        <div className="px-5 py-5 border-b border-svj-border bg-svj-bg/60">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Code *</span>
              <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="ZOMER25" data-testid="admin-discount-code-input" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm uppercase focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Type</span>
              <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="admin-discount-code-type" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
                <option value="percentage">Percentage</option>
                <option value="fixed">Vast bedrag</option>
              </select>
            </label>
            {form.discount_type === "percentage" ? (
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Percentage *</span>
                <input type="number" min="1" max="100" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} data-testid="admin-discount-code-percentage" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
              </label>
            ) : (
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Bedrag (€) *</span>
                <input value={form.fixed_amount_cents} onChange={(e) => setForm({ ...form, fixed_amount_cents: e.target.value })} placeholder="5,00" data-testid="admin-discount-code-fixed-amount" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
              </label>
            )}
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Max. aantal keer gebruik</span>
              <input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Onbeperkt" data-testid="admin-discount-code-max-uses" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Geldig vanaf</span>
              <input type="datetime-local" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} data-testid="admin-discount-code-valid-from" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Geldig t/m</span>
              <input type="datetime-local" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="admin-discount-code-valid-until" className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </label>
          </div>
          <p className="mt-3 text-[11px] text-slate-400">Na het verlopen van de periode of het bereiken van het maximale gebruik wordt de code automatisch geweigerd bij het afrekenen.</p>
          <div className="mt-4 flex gap-2 justify-end">
            <button type="button" onClick={() => { setAdding(false); setForm(emptyDiscountCodeForm()); }} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-sm font-semibold px-4 py-2 inline-flex items-center gap-1">
              <X className="h-4 w-4" /> Annuleren
            </button>
            <button type="button" onClick={create} disabled={creating} data-testid="admin-discount-code-create" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 py-2 inline-flex items-center gap-2">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Code aanmaken
            </button>
          </div>
        </div>
      )}

      {loading && <div className="px-5 py-6 text-sm text-slate-400">Laden…</div>}

      {!loading && items.length === 0 && !adding && (
        <div className="px-5 py-10 text-center" data-testid="admin-discount-codes-empty">
          <Tag className="h-6 w-6 text-svj-cyan mx-auto" />
          <div className="mt-2 text-white font-semibold text-sm">Nog geen kortingscodes</div>
          <p className="mt-1 text-xs text-slate-400">Voeg een code toe die bezoekers bij het afrekenen kunnen invoeren.</p>
        </div>
      )}

      {!loading && items.map((row) => (
        <DiscountCodeRow key={row.id} row={row} eventId={eventId} authAxios={authAxios} onChanged={load} />
      ))}
    </div>
  );
}

export default function AdminEventTicketTypes({ eventId, companionAppEnabled = false, multiDayRegistrationEnabled = false, timeslotBookingEnabled = false, timeslotManualBookingsEnabled = false }) {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/ticket-types`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const ticketTypeNames = useMemo(
    () => Object.fromEntries(items.map((tt) => [tt.id, tt.name])),
    [items],
  );

  const create = async () => {
    const cents = parseEuros(form.price_euros);
    if (!form.name.trim()) return toast.error("Naam is verplicht");
    if (cents == null) return toast.error("Ongeldige prijs");
    setCreating(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        category: form.category.trim() || null,
        price_cents: cents,
        vat_rate: Number(form.vat_rate) || 0,
        capacity: form.capacity === "" ? null : Number(form.capacity),
        sale_starts_at: form.sale_starts_at ? toIso(form.sale_starts_at) : null,
        sale_ends_at: form.sale_ends_at ? toIso(form.sale_ends_at) : null,
        sort_order: Number(form.sort_order) || 0,
        active: !!form.active,
        pay_on_location: !!form.pay_on_location,
      };
      await authAxios.post(`/admin/events/${eventId}/ticket-types`, payload);
      toast.success("Tickettype toegevoegd");
      setForm(emptyForm());
      setAdding(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  if (!eventId) {
    return (
      <div className="rounded-2xl border border-dashed border-svj-border p-6 text-sm text-slate-400" data-testid="admin-tt-locked">
        Sla eerst het event op om tickettypes toe te voegen.
      </div>
    );
  }

  return (
    <>
    <div className="rounded-2xl border border-svj-border overflow-hidden bg-svj-card" data-testid="admin-tt-section">
      <div className="px-5 py-4 flex items-center justify-between border-b border-svj-border">
        <div className="flex items-center gap-2">
          <Ticket className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Tickettypes</div>
          <span className="text-xs text-slate-400">({items.length})</span>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} data-testid="admin-tt-add" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Tickettype
          </button>
        )}
      </div>

      {loading && <div className="px-5 py-6 text-sm text-slate-400">Laden…</div>}

      {!loading && items.length === 0 && !adding && (
        <div className="px-5 py-10 text-center" data-testid="admin-tt-empty">
          <Ticket className="h-6 w-6 text-svj-cyan mx-auto" />
          <div className="mt-2 text-white font-semibold text-sm">Nog geen tickettypes</div>
          <p className="mt-1 text-xs text-slate-400">Voeg minimaal één tickettype toe voordat je publiceert.</p>
        </div>
      )}

      {!loading && items.map((tt) => (
        <TypeRow key={tt.id} tt={tt} onSaved={load} onDeleted={load} authAxios={authAxios} />
      ))}

      {adding && (
        <div className="px-5 py-5 bg-svj-bg/60 border-t border-svj-border">
          <TicketTypeFields form={form} setForm={setForm} idPrefix="new-tt" />
          <div className="mt-4 flex gap-2 justify-end">
            <button type="button" onClick={() => { setAdding(false); setForm(emptyForm()); }} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-sm font-semibold px-4 py-2 inline-flex items-center gap-1">
              <X className="h-4 w-4" /> Annuleren
            </button>
            <button type="button" onClick={create} disabled={creating} data-testid="admin-tt-create" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 py-2 inline-flex items-center gap-2">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Toevoegen
            </button>
          </div>
        </div>
      )}
    </div>

    <div className="mt-6">
      <ManualTicketSection
        eventId={eventId}
        items={items}
        companionAppEnabled={companionAppEnabled}
        multiDayRegistrationEnabled={multiDayRegistrationEnabled}
        timeslotBookingEnabled={timeslotBookingEnabled}
        timeslotManualBookingsEnabled={timeslotManualBookingsEnabled}
        authAxios={authAxios}
      />
    </div>

    <div className="mt-6">
      <WaitlistSection eventId={eventId} ticketTypeNames={ticketTypeNames} authAxios={authAxios} />
    </div>

    <div className="mt-6">
      <DiscountCodesSection eventId={eventId} authAxios={authAxios} />
    </div>
    </>
  );
}
